import React, { useState, Fragment, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './CompareResults.css';
import { searchOptionToLaws, getSearchOptionLabel, getSearchOptionCategory } from '../services/searchOptionLabels';
import { negativeLaws, getEnhancedLawLabel } from '../services/enhancedLawLabels';

// Helper functions
// const invert = (s) => {
//   // Handle string values from database
//   if (s === 'yes' || s === 1 || s === '1') return 'no';
//   if (s === 'no' || s === 0 || s === '0') return 'yes';
//   return 'partial';
// };

const summarise = (arr) => {
  if (arr.every((s) => s === 'yes')) return 'yes';
  if (arr.some((s) => s === 'yes' || s === 'partial')) return 'partial';
  return 'no';
};

const nearMeUrl = (zip) =>
  `https://www.lgbtnearme.org/copy-of-result/index.html?zip=${zip}&miles=25&category=All+Categories`;

// Helper function to get city-wide data tooltip
const getCityWideTooltip = (location) => {
  if (location.cityWideData) {
    if (location.isWeightedAverage) {
      return `City-wide weighted average calculation for ${location.city}, ${location.state_id}`;
    } else {
      return `City-wide average calculation for ${location.city}, ${location.state_id}`;
    }
  }
  return null;
};

// Tooltip component (copied from ResultsScreen)
const Tooltip = ({ children, content, preferredSide = 'right' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualSide, setActualSide] = useState(preferredSide);
  const triggerRef = useRef(null);
  const tooltipRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  useEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let side = preferredSide;
      let top = 0;
      let left = 0;
      
      // Calculate available space on each side
      const spaceRight = viewportWidth - triggerRect.right;
      const spaceLeft = triggerRect.left;
      
      // Determine best side based on available space
      if (preferredSide === 'right' && spaceRight < tooltipRect.width + 10) {
        side = spaceLeft > tooltipRect.width + 10 ? 'left' : 'top';
      } else if (preferredSide === 'left' && spaceLeft < tooltipRect.width + 10) {
        side = spaceRight > tooltipRect.width + 10 ? 'right' : 'top';
      }
      
      // Calculate position based on chosen side
      switch (side) {
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.right + 8;
          break;
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.left - tooltipRect.width - 8;
          break;
        case 'top':
          top = triggerRect.top - tooltipRect.height - 8;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        case 'bottom':
          top = triggerRect.bottom + 8;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          break;
        default:
          // Default to right side
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.right + 8;
          break;
      }
      
      // Ensure tooltip stays within viewport
      top = Math.max(5, Math.min(top, viewportHeight - tooltipRect.height - 5));
      left = Math.max(5, Math.min(left, viewportWidth - tooltipRect.width - 5));
      
      setPosition({ top, left });
      setActualSide(side);
    }
  }, [isVisible, preferredSide]);

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, 200); // Small delay to allow moving to tooltip
  };

  const handleTooltipMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleTooltipMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ position: 'relative', cursor: 'help' }}
      >
        {children}
      </span>
      {isVisible && (
        <div
          ref={tooltipRef}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 9999,
            pointerEvents: 'auto'
          }}
          className={`tooltip-container ${actualSide}`}
        >
          <div className="tooltip-content">
            {content}
        </div>
          <div className={`tooltip-arrow ${actualSide}`} />
        </div>
      )}
    </>
  );
};

// CompareDebugger component
const CompareDebugger = ({ locations }) => {
  const [showDebug, setShowDebug] = useState(false);
  const [showIncomeDetails, setShowIncomeDetails] = useState(true);

  if (!showDebug) {
    return (
      <div style={{ 
        background: '#e8f4fd', 
        padding: '0.75rem', 
        margin: '1rem 0', 
        borderRadius: '8px',
        textAlign: 'center',
        border: '2px solid #0066cc'
      }}>
        <button 
          onClick={() => setShowDebug(true)}
          style={{
            background: '#0066cc',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          🔍 Show Debug: City-Wide Data Calculations
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      background: '#f8f9fa', 
      padding: '1rem', 
      margin: '0.5rem 0', 
      borderRadius: '8px',
      fontSize: '0.85rem',
      maxHeight: '600px',
      overflow: 'auto',
      border: '2px solid #0066cc'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h4 style={{ margin: 0 }}>🔍 Debug: City-Wide Income & Rent Calculations</h4>
        <button 
          onClick={() => setShowDebug(false)}
          style={{
            background: '#dc3545',
            color: 'white',
            border: 'none',
            padding: '0.25rem 0.5rem',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.8rem'
          }}
        >
          ✕ Hide
        </button>
      </div>

      {/* Summary */}
      <div style={{ background: '#fff3cd', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
        <strong>📊 Comparison Summary:</strong>
        <div style={{ marginTop: '0.5rem' }}>
          <div>Location 1: {locations[0].city}, {locations[0].state_id}</div>
          <div>Location 2: {locations[1].city}, {locations[1].state_id}</div>
        </div>
      </div>

      {/* Income/Rent Calculation Details */}
      <div style={{ background: '#F0F9FF', padding: '0.75rem', borderRadius: '4px', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
          <strong>💰 Income & Rent Calculation Details:</strong>
          <button
            onClick={() => setShowIncomeDetails(!showIncomeDetails)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8rem',
              color: '#0284C7'
            }}
          >
            {showIncomeDetails ? '▼ Hide' : '▶ Show'}
          </button>
        </div>
        
        {showIncomeDetails && (
          <div style={{ marginTop: '0.5rem' }}>
            {locations.map((loc, idx) => (
              <div key={idx} style={{ 
                marginBottom: '1rem', 
                padding: '0.5rem', 
                background: idx === 0 ? '#FFF8E1' : '#E3F2FD',
                borderRadius: '4px',
                border: `1px solid ${idx === 0 ? '#FFC107' : '#2196F3'}`
              }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                  📍 Location {idx + 1}: {loc.city}, {loc.state_id}
                </div>
                
                {loc.cityWideData ? (
                  <div style={{ fontSize: '0.8rem' }}>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Calculation Method:</strong> {loc.isWeightedAverage ? 
                        '📊 Weighted Average (Population-based)' : 
                        '📈 Simple Average (No population data)'}
                    </div>
                    
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong>Data Source:</strong> City-wide calculation across all ZIP codes
                    </div>
                    
                    {loc.isWeightedAverage ? (
                      <>
                        <div style={{ marginLeft: '1rem', color: '#059669' }}>
                          <div>✅ Total City Population: {loc.cityPopulation?.toLocaleString() || 'N/A'}</div>
                          <div>✅ Population with Income Data: {loc.incomePopulation?.toLocaleString() || 'N/A'}</div>
                          <div>✅ Population with Rent Data: {loc.rentPopulation?.toLocaleString() || 'N/A'}</div>
                          <div>✅ ZCTA ZIP Codes Used: {loc.zctaCount || 'N/A'}</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ marginLeft: '1rem', color: '#DC2626' }}>
                          <div>⚠️ No population data available - using simple average</div>
                          <div>📍 Income data points: {loc.incomeDataPoints || 0}</div>
                          <div>📍 Rent data points: {loc.rentDataPoints || 0}</div>
                          <div>📍 ZCTA ZIP Codes Found: {loc.zctaCount || 0}</div>
                        </div>
                      </>
                    )}
                    
                    <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #E5E7EB' }}>
                      <strong>Results:</strong>
                      <div style={{ marginLeft: '1rem' }}>
                        <div>💵 Median Income: ${loc.median_household_income?.toLocaleString() || loc.income_household_median?.toLocaleString() || 'No Data'}</div>
                        <div>🏠 Median Rent: ${loc.rent_median?.toLocaleString() || 'No Data'}</div>
                      </div>
                    </div>

                    <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#6B7280' }}>
                      <strong>ZIP Code Used:</strong> {loc.zip} (Representative ZIP for {loc.city})
                    </div>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.8rem', color: '#DC2626' }}>
                    ❌ No city-wide data flag set (this might be an error)
                  </div>
                )}
              </div>
            ))}
            
            {/* Comparison calculations */}
            <div style={{ 
              marginTop: '1rem',
              padding: '0.5rem',
              background: '#F3F4F6',
              borderRadius: '4px'
            }}>
              <strong>📊 Comparison Calculations:</strong>
              <div style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
                {(() => {
                  const income1 = locations[0].median_household_income || locations[0].income_household_median;
                  const income2 = locations[1].median_household_income || locations[1].income_household_median;
                  const rent1 = locations[0].rent_median;
                  const rent2 = locations[1].rent_median;
                  
                  const incomeDiff = income1 && income2 ? ((income2 - income1) / income1 * 100).toFixed(1) : 'N/A';
                  const rentDiff = rent1 && rent2 ? ((rent2 - rent1) / rent1 * 100).toFixed(1) : 'N/A';
                  
                  return (
                    <>
                      <div>
                        <strong>Income Difference:</strong> {
                          incomeDiff !== 'N/A' ? (
                            <span style={{ color: parseFloat(incomeDiff) > 0 ? '#059669' : '#DC2626' }}>
                              {incomeDiff}% ({parseFloat(incomeDiff) > 0 ? 'Higher' : 'Lower'} in Location 2)
                            </span>
                          ) : 'Cannot calculate'
                        }
                      </div>
                      <div>
                        <strong>Rent Difference:</strong> {
                          rentDiff !== 'N/A' ? (
                            <span style={{ color: parseFloat(rentDiff) > 0 ? '#DC2626' : '#059669' }}>
                              {rentDiff}% ({parseFloat(rentDiff) > 0 ? 'More Expensive' : 'Cheaper'} in Location 2)
                            </span>
                          ) : 'Cannot calculate'
                        }
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>
            
            <div style={{ 
              fontSize: '0.75rem', 
              color: '#6B7280', 
              marginTop: '0.5rem',
              padding: '0.5rem',
              background: '#F9FAFB',
              borderRadius: '4px'
            }}>
              <strong>ℹ️ About the calculations:</strong>
              <ul style={{ margin: '0.25rem 0 0 1rem', paddingLeft: '1rem' }}>
                <li><strong>Weighted Average:</strong> Uses population data to give more weight to ZIP codes with more residents</li>
                <li><strong>Simple Average:</strong> Used when population data is missing (all ZIP codes weighted equally)</li>
                <li><strong>ZCTA ZIP Codes:</strong> Only ZIP Code Tabulation Areas with census data are included</li>
                <li><strong>City-Wide Data:</strong> Always calculated for all cities to ensure fair comparisons</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Raw data display */}
      <div style={{ background: '#e8f5e9', padding: '0.75rem', borderRadius: '4px' }}>
        <strong>📢 Raw Data Values:</strong>
        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #4CAF50' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Field</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Location 1</th>
                <th style={{ padding: '0.5rem', textAlign: 'right' }}>Location 2</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '0.25rem' }}>City</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[0].city}</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[1].city}</td>
              </tr>
              <tr style={{ background: '#f5f5f5' }}>
                <td style={{ padding: '0.25rem' }}>State</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[0].state_id}</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[1].state_id}</td>
              </tr>
              <tr>
                <td style={{ padding: '0.25rem' }}>ZIP Code</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[0].zip}</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[1].zip}</td>
              </tr>
              <tr style={{ background: '#f5f5f5' }}>
                <td style={{ padding: '0.25rem' }}>Median Income</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>
                  ${(locations[0].median_household_income || locations[0].income_household_median || 0).toLocaleString()}
                </td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>
                  ${(locations[1].median_household_income || locations[1].income_household_median || 0).toLocaleString()}
                </td>
              </tr>
              <tr>
                <td style={{ padding: '0.25rem' }}>Median Rent</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>
                  ${(locations[0].rent_median || 0).toLocaleString()}
                </td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>
                  ${(locations[1].rent_median || 0).toLocaleString()}
                </td>
              </tr>
              <tr style={{ background: '#f5f5f5' }}>
                <td style={{ padding: '0.25rem' }}>Community Type</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[0].community_type || 'Unknown'}</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[1].community_type || 'Unknown'}</td>
              </tr>
              <tr>
                <td style={{ padding: '0.25rem' }}>City-Wide Data?</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[0].cityWideData ? '✅ Yes' : '❌ No'}</td>
                <td style={{ padding: '0.25rem', textAlign: 'right' }}>{locations[1].cityWideData ? '✅ Yes' : '❌ No'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const CompareResults = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const [printMode, setPrintMode] = useState(false);
  const [viewMode, setViewMode] = useState('basic'); // 'basic' or 'advanced'
  const [openSearchOptions, setOpenSearchOptions] = useState({});
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const [loading] = useState(false);
  const [err] = useState(null);
  const [resourcesLoading, setResourcesLoading] = useState({});
  const [resourcesData, setResourcesData] = useState({});
  const tableRef = useRef(null);
  const stickyRef = useRef(null);
  const location1 = state?.location1;
  const location2 = state?.location2;
  const inputMethod = state?.inputMethod;

  // Update purple squares height when view mode changes
  useEffect(() => {
    const purpleSquare1 = document.querySelector('.cr-purple-square');
    const purpleSquare2 = document.querySelector('.cr-purple-square-2');
    
    if (purpleSquare1 && purpleSquare2) {
      const viewportWidth = window.innerWidth;
      
      if (viewMode === 'advanced') {
        // Different heights based on screen size for advanced view
        if (viewportWidth >= 1920) {
          // Monitor size
          purpleSquare1.style.height = '3110px';
          purpleSquare2.style.height = '3110px';
        } else if (viewportWidth >= 1400 && viewportWidth < 1920) {
          // MacBook size - adjust this height as needed
          purpleSquare1.style.height = '3450px'; // Example: shorter for MacBook
          purpleSquare2.style.height = '3450px';
        } else {
          // Smaller screens
          purpleSquare1.style.height = '3250px';
          purpleSquare2.style.height = '3250px';
        }
      } else {
        // Default height for basic view
        purpleSquare1.style.height = '1300px';
        purpleSquare2.style.height = '1300px';
      }
    }
  }, [viewMode]);

  // Fetch community centers data after component mounts
  // Fetch resources after locations are set
  useEffect(() => {
    if (location1 && location2) {
    const fetchAllResources = async () => {
      const allLocations = [location1, location2].filter(Boolean);
      const loadingState = {};
      const initialResourcesData = {};
      
      // Set all to loading initially AND initialize empty data
      allLocations.forEach(loc => {
        if (loc?.zip) {
          loadingState[loc.zip] = true;
          // Initialize with undefined to indicate "not loaded yet"
          initialResourcesData[loc.zip] = {
            totalResources: undefined,
            communityCenters: undefined
          };
        }
      });
      
      // Set both states immediately so UI shows loading
      setResourcesLoading(loadingState);
      setResourcesData(initialResourcesData);
      
      // Fetch resources for each location in parallel
      const resourcePromises = allLocations.map(async (location) => {
        if (!location?.zip) return null;
        
        try {
          const response = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/lgbtnearme-resources/${location.zip}`,
            { 
              signal: AbortSignal.timeout(10000) // 10 second timeout
            }
          );
          
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          
          const data = await response.json();

          // Add debugging to see what the API returns
          console.log(`API response for ${location.zip}:`, data);

          // Fix the resource count extraction
          const resourceCount = 
            data?.total_resources_within_25_miles ||
            data?.totalResourcesWithin25Miles ||
            data?.total_resources ||
            data?.totalResources ||
            data?.resources?.length ||
            0;

          // Fix the community centers extraction
          const centers = 
            data?.community_centers || 
            data?.communityCenters || 
            data?.centers ||
            [];

          return {
            zip: location.zip,
            totalResources: resourceCount,
            communityCenters: centers
          };
        } catch (error) {
          console.error(`Failed to fetch resources for ${location.zip}:`, error);
          // Return with zeros (not undefined) to indicate load completed but failed
          return {
            zip: location.zip,
            totalResources: 0,
            communityCenters: []
          };
        }
      });
      
      // Process results as they come in
      const results = await Promise.allSettled(resourcePromises);
      
      const newResourcesData = {};
      const newLoadingState = {};
      
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value?.zip) {
          newResourcesData[result.value.zip] = {
            totalResources: result.value.totalResources,
            communityCenters: result.value.communityCenters
          };
          newLoadingState[result.value.zip] = false;
        } else if (result.status === 'rejected') {
          // Handle rejected promises
          const zip = allLocations.find(loc => 
            result.reason?.message?.includes(loc.zip)
          )?.zip;
          
          if (zip) {
            newResourcesData[zip] = {
              totalResources: 0,
              communityCenters: []
            };
            newLoadingState[zip] = false;
          }
        }
      });
      
      setResourcesData(newResourcesData);
      setResourcesLoading(newLoadingState);
    };
    
    // Start fetching immediately
    fetchAllResources();
    }
  }, [location1, location2]);


  // Get all available search options
  const allSearchOptions = Object.keys(searchOptionToLaws);

  // Utility functions
  const handlePrint = () => {
    setPrintMode(true);
    setTimeout(() => {
      window.print();
      setTimeout(() => setPrintMode(false), 500);
    }, 300);
  };

  const toggleSearchOption = (searchOption) => {
    setOpenSearchOptions((prev) => ({ ...prev, [searchOption]: !prev[searchOption] }));
  };

  const getLawStatus = (location, lawId) => {
    const match = location.laws.find((l) => l.law_id === lawId);
    let status = match?.status || 'no';
    // DON'T invert - VCC data is already correct
    return status;
  };

  const getSearchOptionStatus = (location, searchOption) => {
    const lawIds = searchOptionToLaws[searchOption] || [];
    if (lawIds.length === 0) {
      // Nothing mapped → treat as NO (prevents false ✅)
      console.warn(`No laws mapped for search option: ${searchOption}`);
      return 'no';
    }
    const statuses = lawIds.map((lawId) => getLawStatus(location, lawId));
    return summarise(statuses);
  };
  

  // NEW APPROACH: Sticky header with proper positioning
  useEffect(() => {
    const handleScroll = () => {
      const tableContainer = document.querySelector('.cr-table-container');
      if (!tableContainer) return;

      const rect = tableContainer.getBoundingClientRect();
      const shouldStick = rect.top <= 0 && rect.bottom > 100;

      if (shouldStick && !isHeaderStuck) {
        setIsHeaderStuck(true);
      } else if (!shouldStick && isHeaderStuck) {
        setIsHeaderStuck(false);
      }
    };

    // Update the handleResize function in the useEffect
    const handleResize = () => {
      if (stickyRef.current && tableRef.current) {
        const tableRect = tableRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
    
        // Calculate the center position
        const tableCenter = tableRect.left + tableRect.width / 2;
        const viewportCenter = viewportWidth / 2;
        const offset = tableCenter - viewportCenter;
    
        // Apply positioning based on screen size
        if (viewportWidth >= 1920) {
          // For large monitors, shift right by a larger amount
          stickyRef.current.style.left = `calc(50% + ${offset}px + 3.7% + 200px)`;
        } else if (viewportWidth >= 1400) {
          // For medium-large screens, shift right by a moderate amount
          stickyRef.current.style.left = `calc(50% + ${offset}px - 385px)`;
        } else {
          // For smaller screens, keep it centered or slight shift
          stickyRef.current.style.left = `calc(50% + ${offset}px - 460px)`;
        }
    
        // Set the width to match the table
        stickyRef.current.style.width = `${tableRect.width}px`;
      }
    
      // Move purple squares based on ALL screen sizes
      const purpleSquare1 = document.querySelector('.cr-purple-square');
      const purpleSquare2 = document.querySelector('.cr-purple-square-2');
      
      if (purpleSquare1 && purpleSquare2) {
        const viewportWidth = window.innerWidth;
        
        if (viewportWidth >= 1920) {
          // Monitor size - shift 50px to the right
          purpleSquare1.style.transform = 'translate(calc(-50% - 280px), -48%)';
          purpleSquare2.style.transform = 'translate(calc(-50% + 520px), -48%)';
        } else if (viewportWidth >= 1400) {
          // MacBook size - keep original position
          purpleSquare1.style.transform = 'translate(-50%, -50%)';
          purpleSquare2.style.transform = 'translate(calc(-50% + 500px), -50%)';
        } else {
          // Smaller screens - keep original position
          purpleSquare1.style.transform = 'translate(-55%, -49%)';
          purpleSquare2.style.transform = 'translate(calc(-50% + 500px), -49%)';
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    // Initial positioning
    handleScroll();
    setTimeout(handleResize, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [isHeaderStuck]);

  // Reusable expanded grid (matches ResultsScreen layout)
  // Reusable expanded grid (matches ResultsScreen layout)
const renderDetailsGrid = (columns, items, itemLabel, itemStatus) => (
  <tr className="cr-details-row">
    <td className="cr-details-cell" colSpan={2 + columns.length}>
      <div className="cr-law-details">
        <div className="cr-law-details-container">
          <div className="cr-law-details-header"><h4>Legal Protections in Detail:</h4></div>

          <div className="cr-law-details-grid" style={{ '--location-count': columns.length }}>
            {items.map((id) => (
              <div key={id} className="cr-law-detail-item">
                <div className="cr-law-detail-label">
                  {itemLabel(id)}
                </div>

                <div className="cr-law-statuses">
                  {columns.map((col, i) => {
                    const raw = itemStatus(col, id);
                    const status = (raw ?? "").toString().toLowerCase();
                    
                    // Determine badge content based on status
                    let badgeContent;
                    if (status === 'yes') {
                      badgeContent = '\u2714'; // Heavy checkmark ✔
                    } else if (status === 'no') {
                      badgeContent = '\u2716'; // Heavy X ✖
                    } else if (status === 'partial') {
                      badgeContent = 'PARTIAL';
                    } else {
                      badgeContent = status.toUpperCase();
                    }
                    
                    return (
                      <div key={`${i}-${id}`} className={`cr-law-status cr-${status}`}>
                        <span className="cr-status-badge">{badgeContent}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </td>
  </tr>
);
  



  // Helper function for resource display with loading state
const getResourceDisplayWithLoading = (col) => {
  const isLoading = resourcesLoading[col.zip];
  const resourceData = resourcesData[col.zip];
  
  // Check if we're still loading (either explicitly loading or no data yet)
  if (isLoading || resourceData === undefined) {
    return (
      <span style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '0.5rem',
        color: '#6B7280',
        fontSize: '0.875rem'
      }}>
        <span className="cr-loading-spinner-small"></span>
        Loading...
      </span>
    );
  }
  
  // Get the count from resourceData (prioritize this over col data)
  const count = resourceData?.totalResources;

  // If resourceData exists but totalResources is undefined, we're still waiting
  if (resourceData && count === undefined) {
    return (
      <span style={{ 
        display: 'inline-flex', 
        alignItems: 'center', 
        gap: '0.5rem',
        color: '#9CA3AF',
        fontSize: '0.875rem',
        fontStyle: 'italic'
      }}>
        Loading resources...
      </span>
    );
  }
  
  // Only show the count if we have actual data
  if (count !== undefined && count !== null) {
    return `${count} Resources`;
  }
  
  // Fallback to col data if available
  if (col.totalResources !== undefined) {
    return `${col.totalResources} Resources`;
  }
  
  return 'View Resources';
};

// Helper function for community centers with loading state
const getCommunityCentersWithLoading = (col) => {
  const isLoading = resourcesLoading[col.zip];
  const resourceData = resourcesData[col.zip];
  
  const centers = resourceData?.communityCenters ?? 
                 resourceData?.community_centers ?? 
                 col.communityCenters ??
                 col.community_centers;
  
  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: '0.5rem',
        color: '#6B7280',
        fontSize: '0.875rem',
        minHeight: '24px'
      }}>
        <span className="cr-loading-spinner-small"></span>
        <span>Loading...</span>
      </div>
    );
  }
  
  if (centers === undefined && !resourceData) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        gap: '0.5rem',
        color: '#9CA3AF',
        fontSize: '0.875rem',
        fontStyle: 'italic',
        minHeight: '24px'
      }}>
        Loading centers...
      </div>
    );
  }
  
  if (!centers || centers.length === 0) {
    return (
      <div className="cr-center-links">
        <span style={{ color: '#666', fontSize: '0.875rem' }}>
          None Available
        </span>
        {/* <a
          href={`https://www.lgbtnearme.org/copy-of-result/index.html?zip=${col.zip}&miles=25&category=Community+Center&type=pridePath`}
          target="_blank"
          rel="noopener noreferrer"
          className="cr-center-link"
          style={{ fontSize: '0.75rem', fontStyle: 'italic' }}
        >
          (Check API)
        </a> */}
      </div>
    );
  }
  
  if (centers.length > 1 || centers[0] === 'lgbtnearme_link') {
    return (
      <div className="cr-center-links">
        <a
          href={`https://www.lgbtnearme.org/copy-of-result/index.html?zip=${col.zip}&miles=25&category=Community+Center&type=pridePath`}
          target="_blank"
          rel="noopener noreferrer"
          className="cr-center-link"
        >
          {centers.length > 1 ? 'Multiple' : 'None Available'}
        </a>
      </div>
    );
  }
  
  return (
    <div className="cr-center-links">
      <a
        href={centers[0].website?.startsWith('http') ? centers[0].website : `https://${centers[0].website}`}
        target="_blank"
        rel="noopener noreferrer"
        className="cr-center-link"
      >
        {centers[0].name}
      </a>
    </div>
  );
};

  // Update positioning when sticky state changes
  useEffect(() => {
    if (isHeaderStuck && stickyRef.current && tableRef.current) {
      const tableRect = tableRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      // Calculate the center position
      const tableCenter = tableRect.left + tableRect.width / 2;
      const viewportCenter = viewportWidth / 2;
      const offset = tableCenter - viewportCenter;

      // Apply positioning based on screen size
      if (viewportWidth >= 1920) {
        // For large monitors, shift right
        stickyRef.current.style.left = `calc(50% + ${offset}px + 3.7%)`;
      } else {
        // For smaller screens, center normally
        stickyRef.current.style.left = `calc(50% + ${offset}px)`;
      }

      // Set the width to match the table
      stickyRef.current.style.width = `${tableRect.width}px`;
      stickyRef.current.style.transform = 'translateX(-50%)';
    }
  }, [isHeaderStuck]);

  // Early return for missing data
  if (!location1 || !location2) {
    return (
      <div className="cr-container">
        <div className="cr-card cr-error">
          <h1>No Comparison Data</h1>
          <p className="cr-error-msg">Unable to load comparison data</p>
          <button onClick={() => navigate('/compare-search')} className="cr-btn">
            Start New Comparison
          </button>
        </div>
      </div>
    );
  }

  // Add error display
  if (err) {
    return (
      <div className="cr-container">
        <div className="cr-card cr-error">
          <h1>Something Went Wrong</h1>
          <p className="cr-error-msg">{err}</p>
          {err && err.includes('Too many comparisons') && (
            <div style={{
              marginTop: '1rem',
              padding: '1rem',
              background: '#FEF3C7',
              borderRadius: '8px',
              border: '1px solid #F59E0B'
            }}>
              <p style={{ margin: 0, color: '#92400E', fontSize: '0.9rem' }}>
                <strong>Why am I seeing this?</strong><br />
                To ensure our service remains fast for everyone, we limit the number of comparisons 
                that can be performed in a short time period. This limit will reset automatically.
              </p>
            </div>
          )}
          <button onClick={() => navigate('/compare-search')} className="cr-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Add loading display
  if (loading) {
    return (
      <div className="cr-container">
        <div className="cr-card">
          <div className="cr-loading">
            <div className="cr-spinner"></div>
            <h2>Comparing Locations...</h2>
            <p>Analyzing LGBTQ+ protections in both areas</p>
          </div>
        </div>
      </div>
    );
  }

  const columns = [location1, location2];

  const getLocationName = (col) => {
    if (inputMethod === 'zipcode' || inputMethod === 'city') {
      return `${col.city?.toUpperCase() || 'UNKNOWN'}, ${col.state_id || col.state}`;
    }
    return col.stateName || col.state_id || col.label;
  };

  // Define basic search buckets based on the actual basic search form
  const basicSearchBuckets = {
    'Non-discrimination in EMPLOYMENT, HOUSING & PUBLIC ACCOMMODATION': [
      'employmentNonDiscrimination',
      'housingNonDiscrimination', 
      'publicAccommodationNonDiscrimination'
    ],
    'Non-discrimination for STATE EMPLOYEES': [
      'stateEmployeeNonDiscrimination'
    ],
    'Inclusive relationship and parental recognition': [
      'secondParentAdoption',
      'adoptionNonDiscrimination',
      'fosterCareNonDiscrimination',
      'assistedReproductionRecognition',
      'lgbtInclusiveSpouseForLeave',
      'lgbtInclusiveChildrenForLeave'
    ],
    'Inclusive healthcare laws & policies': [
      'privateHealthInsuranceNonDiscrimination',
      'shieldLawsTransgenderCare',
      'healthInsuranceTransgenderCoverage',
      'medicaidTransgenderCoverage',
      'stateEmployeeTransgenderBenefits',
      'fertilityPrivateInsurance',
      'fertilityMedicaid',
      'fertilityPreservationPrivate',
      'fertilityPreservationMedicaid',
      'transgenderYouthCare',
      'noReligiousExemptionMedical'
    ],
    'Inclusive gender markers': [
      'driverLicenseGenderMarker',
      'birthCertificateGenderMarker',
      'xOptionDriverLicense',
      'xOptionBirthCertificate',
      'nameChangeProcess'
    ],
    'Inclusive youth protections': [
      'studentNonDiscrimination',
      'localStudentNonDiscrimination',
      'antibullyingLaws',
      'localAntiBullyingLaws',
      'conversionTherapyBan',
      'transgenderSportsParticipation',
      'childWelfareSystemProtections',
      'lgbtInclusiveCurriculum',
      'noParentalOptOut',
      'noForcedOuting',
      'noDontSayGay'
    ],
    'Inclusive criminal justice': [
      'hateCrimeLaws',
      'noPanicDefense',
      'juryServiceNonDiscrimination',
      'noDragRestrictions'
    ],
    'State does NOT ban cities from non-discrimination laws': [
      'allowLocalLaws'
    ],
    'State does NOT ban cities from bathroom laws': [
      'bathroomAccess'
    ],
    'State does NOT ban recognizing transgender people': [
      'noExclusionarySexDefinition'
    ],
    'State does NOT allow religious exemptions': [
      'noReligiousExemptionMarriage',
      'noReligiousExemptionMedical',
      'noReligiousExemptionChildWelfare'
    ]
  };

  // Group search options by category for advanced view
  const advancedSearchCategories = {
    'BROAD PROTECTIONS': [
      'employmentNonDiscrimination',
      'housingNonDiscrimination',
      'publicAccommodationNonDiscrimination',
      'stateEmployeeNonDiscrimination',
      'allowLocalLaws',
      'noReligiousExemptionMarriage'
    ],
    'RELATIONSHIP & PARENTAL RECOGNITION': [
      'secondParentAdoption',
      'adoptionNonDiscrimination',
      'fosterCareNonDiscrimination',
      'assistedReproductionRecognition',
      'lgbtInclusiveSpouseForLeave',
      'lgbtInclusiveChildrenForLeave'
    ],
    'INCLUSIVE HEALTHCARE LAWS & POLICIES': [
      'privateHealthInsuranceNonDiscrimination',
      'shieldLawsTransgenderCare',
      'healthInsuranceTransgenderCoverage',
      'medicaidTransgenderCoverage',
      'stateEmployeeTransgenderBenefits',
      'fertilityPrivateInsurance',
      'fertilityMedicaid',
      'fertilityPreservationPrivate',
      'fertilityPreservationMedicaid',
      'transgenderYouthCare',
      'noReligiousExemptionMedical'
    ],
    'GENDER MARKERS & TRANS POLICIES': [
      'driverLicenseGenderMarker',
      'birthCertificateGenderMarker',
      'xOptionDriverLicense',
      'xOptionBirthCertificate',
      'nameChangeProcess',
      'bathroomAccess',
      'noExclusionarySexDefinition'
    ],
    'LGBT YOUTH': [
      'studentNonDiscrimination',
      'localStudentNonDiscrimination',
      'antibullyingLaws',
      'localAntiBullyingLaws',
      'conversionTherapyBan',
      'transgenderSportsParticipation',
      'childWelfareSystemProtections',
      'lgbtInclusiveCurriculum',
      'noParentalOptOut',
      'noForcedOuting',
      'noDontSayGay',
      'noReligiousExemptionChildWelfare'
    ],
    'CRIMINAL JUSTICE': [
      'hateCrimeLaws',
      'noPanicDefense',
      'juryServiceNonDiscrimination',
      'noDragRestrictions'
    ]
  };

  // Use the appropriate grouping based on view mode
  const groupedSearchOptions = viewMode === 'basic' ? basicSearchBuckets : advancedSearchCategories;

  // Get bucket status
  const getBucketStatus = (location, category) => {
    const searchOptions = groupedSearchOptions[category] || [];
    const statuses = searchOptions.map(option => getSearchOptionStatus(location, option));
    return summarise(statuses);
  };

  // Render the info rows with Income and Rent split
  const renderInfoRows = () => {
    return (
      <>
        {/* Distance Row */}
        <tr className="cr-info-row">
          <td className="cr-category-label">DISTANCE FROM CURRENT</td>
          <td className="cr-empty-cell"></td>
          {columns.map((col, idx) => {
            const val = col.distance;
            let cell;
            if (idx === 0) {
              cell = 'Current';
            } else {
              cell = val === 'N/A' || !val ? 'N/A' : 
                     typeof val === 'string' && val.includes('miles') ? val : `${val} mi`;
            }
            return (
              <td key={idx} className="cr-location-data">
                {cell}
              </td>
            );
          })}
        </tr>

        {/* Income Comparison Row */}
        <tr className="cr-info-row">
          <td className="cr-category-label">INCOME COMPARISON</td>
          <td className="cr-empty-cell"></td>
          {columns.map((col, idx) => {
            const currentIncome = col.income_household_median || col.median_household_income;
            const cityWideTooltip = getCityWideTooltip(col);
            
            if (idx === 0) {
              // First location - show actual income value
              if (currentIncome) {
                return (
                  <td key={idx} className="cr-location-data">
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      ${currentIncome.toLocaleString()}
                      <Tooltip 
                        content={
                          cityWideTooltip ? (
                            <div>
                              <div>Median Household Income</div>
                              <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                {cityWideTooltip}
                              </div>
                            </div>
                          ) : (
                            'Median Household Income'
                          )
                        } 
                        preferredSide="right"
                      >
                        <span className="info-icon">ⓘ</span>
                      </Tooltip>
                    </span>
                  </td>
                );
              } else {
                return <td key={idx} className="cr-location-data">No Data</td>;
              }
            } else {
              // Second location - show actual value AND comparison
              const firstLocationIncome = columns[0].income_household_median || columns[0].median_household_income;
              
              if (!currentIncome) {
                return <td key={idx} className="cr-location-data">No Data</td>;
              } else if (!firstLocationIncome) {
                // If first location has no data, just show the value
                return (
                  <td key={idx} className="cr-location-data">
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      ${currentIncome.toLocaleString()}
                      <Tooltip 
                        content={
                          cityWideTooltip ? (
                            <div>
                              <div>Median Household Income</div>
                              <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                {cityWideTooltip}
                              </div>
                            </div>
                          ) : (
                            'Median Household Income'
                          )
                        } 
                        preferredSide="right"
                      >
                        <span className="info-icon">ⓘ</span>
                      </Tooltip>
                    </span>
                  </td>
                );
              } else {
                const diff = ((currentIncome - firstLocationIncome) / firstLocationIncome) * 100;
                const absDiff = Math.abs(diff);
                
                let comparisonText;
                let comparisonClass = "";
                
                if (absDiff < 0.5) {
                  comparisonText = "0% Difference";
                } else if (diff > 0) {
                  comparisonText = `${absDiff.toFixed(0)}% Higher`;
                  comparisonClass = "income-higher";
                } else {
                  comparisonText = `${absDiff.toFixed(0)}% Lower`;
                  comparisonClass = "income-lower";
                }
                
                return (
                  <td key={idx} className="cr-location-data">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        ${currentIncome.toLocaleString()}
                        <Tooltip 
                          content={
                            cityWideTooltip ? (
                              <div>
                                <div>Median Household Income</div>
                                <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                  {cityWideTooltip}
                                </div>
                              </div>
                            ) : (
                              'Median Household Income'
                            )
                          } 
                          preferredSide="right"
                        >
                          <span className="info-icon">ⓘ</span>
                        </Tooltip>
                      </span>
                      {/* <span className={comparisonClass} style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                        ({comparisonText})
                      </span> */}
                    </div>
                  </td>
                );
              }
            }
          })}
        </tr>

        {/* Rent Comparison Row */}
        <tr className="cr-info-row">
          <td className="cr-category-label">RENT COMPARISON</td>
          <td className="cr-empty-cell"></td>
          {columns.map((col, idx) => {
            const currentRent = col.rent_median;
            const cityWideTooltip = getCityWideTooltip(col);
            
            if (idx === 0) {
              // First location - show actual rent value
              if (currentRent) {
                return (
                  <td key={idx} className="cr-location-data">
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      ${currentRent.toLocaleString()}
                      <Tooltip 
                        content={
                          cityWideTooltip ? (
                            <div>
                              <div>Median Monthly Rent</div>
                              <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                {cityWideTooltip}
                              </div>
                            </div>
                          ) : (
                            'Median Monthly Rent'
                          )
                        } 
                        preferredSide="right"
                      >
                        <span className="info-icon">ⓘ</span>
                      </Tooltip>
                    </span>
                  </td>
                );
              } else {
                return <td key={idx} className="cr-location-data">No Data</td>;
              }
            } else {
              // Second location - show actual value AND comparison
              const firstLocationRent = columns[0].rent_median;
              
              if (!currentRent) {
                return <td key={idx} className="cr-location-data">No Data</td>;
              } else if (!firstLocationRent) {
                // If first location has no data, just show the value
                return (
                  <td key={idx} className="cr-location-data">
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      ${currentRent.toLocaleString()}
                      <Tooltip 
                        content={
                          cityWideTooltip ? (
                            <div>
                              <div>Median Monthly Rent</div>
                              <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                {cityWideTooltip}
                              </div>
                            </div>
                          ) : (
                            'Median Monthly Rent'
                          )
                        } 
                        preferredSide="right"
                      >
                        <span className="info-icon">ⓘ</span>
                      </Tooltip>
                    </span>
                  </td>
                );
              } else {
                const diff = ((currentRent - firstLocationRent) / firstLocationRent) * 100;
                const absDiff = Math.abs(diff);
                
                let comparisonText;
                let comparisonClass = "";
                
                if (absDiff < 0.5) {
                  comparisonText = "0% Difference";
                } else if (diff > 0) {
                  comparisonText = `${absDiff.toFixed(0)}% More Expensive`;
                  comparisonClass = "rent-expensive";
                } else {
                  comparisonText = `${absDiff.toFixed(0)}% Cheaper`;
                  comparisonClass = "rent-cheaper";
                }
                
                return (
                  <td key={idx} className="cr-location-data">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        ${currentRent.toLocaleString()}
                        <Tooltip 
                          content={
                            cityWideTooltip ? (
                              <div>
                                <div>Median Monthly Rent</div>
                                <div style={{ marginTop: '4px', fontSize: '0.9em', fontStyle: 'italic' }}>
                                  {cityWideTooltip}
                                </div>
                              </div>
                            ) : (
                              'Median Monthly Rent'
                            )
                          } 
                          preferredSide="right"
                        >
                          <span className="info-icon">ⓘ</span>
                        </Tooltip>
                      </span>
                      {/* <span className={comparisonClass} style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                        ({comparisonText})
                      </span> */}
                    </div>
                  </td>
                );
              }
            }
          })}
        </tr>

        {/* Population Row */}
        <tr className="cr-info-row">
          <td className="cr-category-label">CITY POPULATION</td>
          <td className="cr-empty-cell"></td>
          {columns.map((col, idx) => {
            // Check for different possible field names your backend might use
            const population = col.city_population || col.population || col.totalPopulation;
            
            let cell;
            if (population) {
              // Make sure it's a number and format with commas
              const popNumber = typeof population === 'string' ? parseInt(population, 10) : population;
              cell = popNumber.toLocaleString('en-US');
            } else {
              // Fallback if no population data
              cell = 'N/A';
            }
            
            return (
              <td key={idx} className="cr-location-data">
                {cell}
              </td>
            );
          })}
        </tr>

        {/* Community Centers Row */}
        <tr className="cr-info-row">
          <td className="cr-category-label">COMMUNITY CENTERS</td>
          <td className="cr-empty-cell"></td>
          {columns.map((col, idx) => {
            const cell = getCommunityCentersWithLoading(col);
            return (
              <td key={idx} className="cr-location-data">
                {cell}
              </td>
            );
          })}
        </tr>

        {/* Local LGBT Resources Row */}
        <tr className="cr-info-row">
          <td className="cr-category-label">Local LGBT Resources</td>
          <td className="cr-empty-cell"></td>
          {columns.map((col, idx) => {
            const displayText = getResourceDisplayWithLoading(col);
            return (
              <td key={idx} className="cr-location-data">
                <a
                  href={nearMeUrl(col.zip)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cr-center-link"
                >
                  {displayText}
                </a>
              </td>
            );
          })}
        </tr>
      </>
    );
  };

  const renderSearchOptionRow = (searchOption) => {
    const expanded = openSearchOptions[searchOption];
    const lawIds = searchOptionToLaws[searchOption] || [];
    
    return (
      <Fragment key={searchOption}>
        <tr 
          className={`cr-bucket-row ${expanded ? 'cr-expanded' : ''}`} 
          onClick={() => viewMode === 'advanced' && lawIds.length > 1 ? toggleSearchOption(searchOption) : null}
          style={{ cursor: viewMode === 'advanced' && lawIds.length > 1 ? 'pointer' : 'default' }}
        >
          <td className="cr-category-label">
            <span className="cr-category-text">{getSearchOptionLabel(searchOption)}</span>
          </td>
          {viewMode === 'advanced' && lawIds.length > 1 ? (
            <td className="cr-view-details-cell">
              <button 
                className="cr-view-details-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSearchOption(searchOption);
                }}
              >
                {expanded ? 'HIDE\nDETAILS' : 'VIEW\nDETAILS'}
              </button>
            </td>
          ) : (
            <td className="cr-empty-cell"></td>
          )}
          {columns.map((col, idx) => {
            const status = getSearchOptionStatus(col, searchOption);
            return (
              <td key={idx} className={`cr-status-cell cr-${status}`}>
                {/* Status indicator handled by CSS ::before */}
              </td>
            );
          })}
        </tr>
        {expanded && viewMode === 'advanced' && lawIds.length > 1 && (
          <tr>
            <td colSpan={columns.length + 2} className="cr-details-cell">
              {lawIds.length <= 1 ? (
                <div className="cr-no-details">
                  <p>
                    ℹ️ This protection category contains only one law, which is already shown above.
                  </p>
                </div>
              ) : (
                <div className="cr-law-details">
                  <table className="cr-law-details-table">
                    <thead>
                      <tr>
                        <th>Specific Protection</th>
                        <th colSpan={columns.length}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lawIds.map((lawId) => (
                        <tr key={lawId}>
                          <td className="cr-law-label">
                            {getEnhancedLawLabel(lawId)}
                            {negativeLaws.has(lawId) && (
                              <span className="cr-inverted-tag">(inverted)</span>
                            )}
                          </td>
                          {columns.map((col) => {
                            const status = getLawStatus(col, lawId);
                            return (
                              <td key={`${col.id}-${lawId}`} className={`cr-status-cell cr-small cr-${status}`}>
                                {/* Status indicator handled by CSS ::before */}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const renderBucketRow = (category) => {
    // Only render buckets in Basic view
    if (viewMode !== "basic") return null;
  
    const expanded = !!openSearchOptions[category];
    const searchOptions = groupedSearchOptions[category] || [];
  
    // Main-row rollup dot per column (unchanged logic)
    const statusForColumn = (col) => getBucketStatus(col, category);
  
    // Compute once: options that actually map to laws
    const mappedOptions = searchOptions.filter((opt) => searchOptionToLaws?.[opt]);
    const uniqueLawIds = new Set();
    mappedOptions.forEach((opt) => (searchOptionToLaws[opt] || []).forEach((id) => uniqueLawIds.add(id)));
  
    const onlyOneItem = uniqueLawIds.size <= 1; // mimic your previous “no details” rule
  
    return (
      <Fragment key={category}>
        <tr
          className={`cr-bucket-row ${expanded ? "cr-expanded" : ""}`}
          onClick={() => toggleSearchOption(category)}
          style={{ cursor: "pointer" }}
        >
          <td className="cr-category-label cr-expandable">
            <span className="cr-category-text">{category}</span>
          </td>
  
          <td className="cr-view-details-cell">
            <button
              className="cr-view-details-btn"
              onClick={(e) => {
                e.stopPropagation();
                toggleSearchOption(category);
              }}
            >
              {expanded ? "HIDE\nDETAILS" : "VIEW\nDETAILS"}
            </button>
          </td>
  
          {columns.map((col, idx) => {
            const status = statusForColumn(col); // "yes" | "no" | "partial"
            return <td key={idx} className={`cr-status-cell cr-${status}`} />;
          })}
        </tr>
  
        {expanded && (
          onlyOneItem ? (
            <tr className="cr-details-row">
              <td className="cr-details-cell" colSpan={2 + columns.length}>
                <div className="cr-no-details">
                  <p>ℹ️ This category contains only one protection item, which is already shown above.</p>
                </div>
              </td>
            </tr>
          ) : (
            // ResultsScreen-like grid (label + per-location badges)
            renderDetailsGrid(
              columns,
              mappedOptions,                               // items to render
              (opt) => getSearchOptionLabel(opt) || opt,  // label for each row
              (col, opt) => getSearchOptionStatus(col, opt) // status per (location, option)
            )
          )
        )}
      </Fragment>
    );
  };
  

  return (
    <div className={`cr-container ${printMode ? 'cr-print' : ''}`}>
      <div className="cr-purple-square"></div>
      <div className="cr-purple-square-2"></div>
      <div className="cr-wave-wrapper"></div>
      <div className="cr-card">
        <div className="cr-header">
          <h1>Location Comparison Results</h1>
          <p>Side-by-side comparison of LGBTQ+ protections</p>
          <div className="cr-header-actions">
            <button className="cr-btn cr-secondary" onClick={handlePrint}>
              🖨️ Print Results
            </button>
            <button className="cr-btn" onClick={() => navigate('/compare-search')}>
              🔍 New Comparison
            </button>
          </div>
        </div>
        
        {/* CompareDebugger here */}
        {/* <CompareDebugger locations={columns} /> */}

        <div className="cr-view">
          {/* Slider Toggle */}
          <div className="cr-toggle-container">
            <div className="cr-toggle-slider">
              <div className={`cr-slider-track ${viewMode}`}>
                <div className="cr-slider-thumb" />
              </div>
              <button
                className={`cr-toggle-option ${viewMode === 'basic' ? 'cr-active' : ''}`}
                onClick={() => setViewMode('basic')}
              >
                <span className="cr-toggle-icon">📊</span>
                <span className="cr-toggle-label">Basic</span>
              </button>
              <button
                className={`cr-toggle-option ${viewMode === 'advanced' ? 'cr-active' : ''}`}
                onClick={() => setViewMode('advanced')}
              >
                <span className="cr-toggle-icon">🔍</span>
                <span className="cr-toggle-label">Advanced</span>
              </button>
            </div>
            <p className="cr-toggle-desc">
              {viewMode === 'basic' 
                ? 'Showing protection categories only' 
                : 'Showing all individual protections by category'}
            </p>
          </div>

          {/* Floating Sticky Header */}
          {isHeaderStuck && (
            <div className="cr-sticky-wrapper" ref={stickyRef}>
              <table className="cr-table cr-sticky-table">
                <thead>
                  <tr>
                    <th className="cr-protection-header">Protection Category</th>
                    <th className="cr-action-header"></th>
                    {columns.map((col, idx) => (
                      <th key={idx} className="cr-location-col">
                        <div className="cr-location-header">
                          <div className={`cr-location-tag ${idx === 0 ? 'cr-current' : 'cr-match'}`}>
                            Location {idx + 1}
                          </div>
                          <div className="cr-location-name">
                            {getLocationName(col)}
                          </div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
              </table>
            </div>
          )}

          <div className={`cr-table-container ${isHeaderStuck ? 'cr-header-stuck' : ''}`} ref={tableRef}>
            <table className="cr-table">
              <thead className={isHeaderStuck ? 'cr-is-stuck' : ''}>
                <tr>
                  <th className="cr-protection-header">Protection Category</th>
                  <th className="cr-action-header"></th>
                  {columns.map((col, idx) => (
                    <th key={idx} className="cr-location-col">
                      <div className="cr-location-header">
                        <div className={`cr-location-tag ${idx === 0 ? 'cr-current' : 'cr-match'}`}>
                          Location {idx + 1}
                        </div>
                        <div className="cr-location-name">
                          {getLocationName(col)}
                        </div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderInfoRows()}
                <tr className="cr-divider-row">
                  <td colSpan={columns.length + 2}>
                    <div className="cr-divider-content">Legal Protections</div>
                  </td>
                </tr>
                {viewMode === 'basic' ? (
                  // Basic view: Show basic search categories only
                  Object.keys(basicSearchBuckets).map(category => renderBucketRow(category))
                ) : (
                  // Advanced view: Show advanced search categories with all options
                  Object.keys(advancedSearchCategories).map(category => (
                    <Fragment key={category}>
                      <tr className="cr-category-header-row">
                        <td colSpan={columns.length + 2}>
                          <div className="cr-category-header">{category}</div>
                        </td>
                      </tr>
                      {advancedSearchCategories[category]
                        .filter(option => searchOptionToLaws[option]) // Only show options that have law mappings
                        .map(option => renderSearchOptionRow(option))}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompareResults;